import { useMemo, useRef, type MutableRefObject, type RefObject } from "react";

/** What the cast follower drives: a media element, plus the autoplay-blocked hook the player fills in. */
export interface MediaCastDriver {
  /** Stable identity; always talks to the current element. */
  castDriver: {
    seek: (ms: number) => void;
    play: () => void;
    pause: () => void;
    setRate: (rate: number) => void;
  };
  /** The player assigns this each render; called when play() is rejected with NotAllowedError. */
  blockedRef: MutableRefObject<() => void>;
}

export function useMediaCastDriver(el: RefObject<HTMLMediaElement | null>): MediaCastDriver {
  const blockedRef = useRef<() => void>(() => {});
  const castDriver = useMemo(
    () => ({
      seek: (ms: number) => {
        if (el.current) el.current.currentTime = ms / 1000;
      },
      play: () => {
        el.current?.play()?.catch((e: { name?: string }) => {
          if (e?.name === "NotAllowedError") blockedRef.current();
        });
      },
      pause: () => el.current?.pause(),
      setRate: (rate: number) => {
        if (el.current) el.current.playbackRate = rate;
      },
    }),
    [el],
  );
  return { castDriver, blockedRef };
}
